import { Helmet } from "react-helmet-async";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";
// Public canonical origin. Kept as a constant here so the SEO layer
// never leaks a preview / staging URL into rel=canonical or og:url tags.
const CANONICAL_ORIGIN = "https://tyrematch.co.nz";

// SEO configuration per page (Feb 2026 rewrite).
//
// Rules enforced by <SEO/>:
//   1) Every entry must set `title`, `description`, `canonicalPath`.
//   2) `canonicalPath` is the SELF path for that URL (never the homepage).
//   3) App-shell / auth pages that must never appear in Google SERPs set
//      `noindex: true` — SEO emits `<meta name="robots" content="noindex, nofollow">`.
//   4) If a title contains "|" it is used verbatim; otherwise " | TyreMatch" is appended.
const SEO_CONFIG = {
  // ------- Indexable marketing pages -------
  home: {
    title: "TyreMatch | Private Tyre Quotes from Local NZ Shops",
    description: "Request once and get private tyre quotes from local NZ shops. Shops can't see each other's prices — compare and book fitment near you.",
    keywords: "private tyre quotes NZ, compare tyres New Zealand, tyre shops near me, wheel alignment, tyre rotation, local tyre shops",
    canonicalPath: "/",
  },
  howItWorks: {
    title: "How private tyre quotes work NZ | TyreMatch",
    description: "Request once, get private quotes from local NZ tyre shops. Shops can't see each other's prices — your details stay hidden until you choose.",
    keywords: "private tyre quotes, how tyrematch works, compare tyre shops NZ, blind quotes tyres",
    canonicalPath: "/how-it-works",
  },
  treadDepth: {
    title: "Tyre tread depth NZ: 1.5 mm legal vs ~3 mm wet-safe",
    description: "NZ's legal minimum tread is 1.5 mm for a WOF — but wet grip falls earlier. How to check tread, when to replace, and how to get local tyre quotes.",
    keywords: "tyre tread depth NZ, WOF tyres, minimum tread NZ, wet weather tyres, 20c coin tyre test",
    canonicalPath: "/tread-depth-nz",
  },
  forShops: {
    title: "Partner with TyreMatch | For NZ Tyre Shops",
    description: "Get inbound quote requests from customers in your area. Set your own price — competitors can't see it. Join TyreMatch as a partner shop.",
    keywords: "tyre shop partner NZ, tyre shop leads, join tyrematch",
    canonicalPath: "/for-shops",
  },
  // City pages set their own canonicalPath dynamically (see CityLandingPage).
  cityLanding: {
    title: "Tyre quotes near you | TyreMatch",
    description: "Get private tyre quotes from local NZ shops in your area. Compare privately and book fitment nearby.",
    keywords: "tyre quotes local, tyre shops, wheel alignment",
    canonicalPath: null, // caller must pass customCanonicalPath
  },

  // ------- App shell (should NOT rank) -------
  quoteRequest: {
    title: "Request Tyre Quotes | TyreMatch",
    description: "Submit a quote request and receive private quotes from local NZ tyre shops.",
    keywords: "request tyre quote, get quotes, tyre services NZ",
    canonicalPath: "/quote-request",
    noindex: true,
  },
  customerDashboard: {
    title: "My Account | TyreMatch",
    description: "Manage your tyre quotes, bookings, and service history in your TyreMatch account.",
    keywords: "tyrematch account",
    canonicalPath: "/customer/dashboard",
    noindex: true,
  },
  customerAccount: {
    title: "My Account Settings | TyreMatch",
    description: "Update your profile, address, and preferences in your TyreMatch account.",
    keywords: "account settings, profile, tyrematch",
    canonicalPath: "/customer/account",
    noindex: true,
  },
  shopDashboard: {
    title: "Shop Dashboard | TyreMatch Partner Portal",
    description: "Manage your tyre shop quotes, orders, and customer requests on TyreMatch.",
    keywords: "tyre shop dashboard, partner portal, manage quotes",
    canonicalPath: "/shop/dashboard",
    noindex: true,
  },
  shopAccount: {
    title: "Shop Account Settings | TyreMatch Partner Portal",
    description: "Manage your shop profile, reviews, and business settings on TyreMatch.",
    keywords: "shop settings, tyre shop profile, business account",
    canonicalPath: "/shop/account",
    noindex: true,
  },
  customerAuth: {
    title: "Login or Register | TyreMatch",
    description: "Create an account or login to compare tyre prices and book services with TyreMatch.",
    keywords: "tyrematch login, register, create account",
    canonicalPath: "/customer/auth",
    noindex: true,
  },
  shopAuth: {
    title: "Tyre Shop Partner Login | TyreMatch",
    description: "Login to your TyreMatch partner account to manage quotes, orders, and grow your tyre business.",
    keywords: "tyre shop login, partner portal, shop registration",
    canonicalPath: "/shop/auth",
    noindex: true,
  },
  admin: {
    title: "Admin Dashboard | TyreMatch",
    description: "TyreMatch administration portal.",
    keywords: "admin, dashboard",
    canonicalPath: "/nedhaavu/dashboard",
    noindex: true,
  },
  shopProfile: {
    title: "Tyre Shop Profile | TyreMatch",
    description: "View shop details, reviews, and ratings on TyreMatch.",
    keywords: "tyre shop profile",
    canonicalPath: "/",
    noindex: true,
  },
  comingSoon: {
    title: "Coming Soon | TyreMatch",
    description: "TyreMatch is launching soon in New Zealand.",
    keywords: "tyrematch launch, coming soon",
    canonicalPath: "/",
    noindex: true,
  },

  // Legacy alias landing pages (kept so /tyres etc still get sensible meta
  // if wired up in future — currently unlinked; not in sitemap).
  tyres: {
    title: "Get Tyre Quotes Online | Compare Tyre Prices NZ | TyreMatch",
    description: "Get multiple quotes from local tyre shops, compare prices, and choose the best option for your vehicle in New Zealand.",
    keywords: "tyre quotes NZ, tyre prices, buy tyres online",
    canonicalPath: "/",
    noindex: true,
  },
  alignment: {
    title: "Wheel Alignment Services Near You | Compare Prices NZ | TyreMatch",
    description: "Find and compare wheel alignment services near you. Get quotes from trusted local providers with TyreMatch.",
    keywords: "wheel alignment NZ",
    canonicalPath: "/",
    noindex: true,
  },
  rotation: {
    title: "Tyre Rotation Services NZ | Compare & Book Online | TyreMatch",
    description: "Extend the life of your tyres with professional rotation services. Compare prices from local tyre shops.",
    keywords: "tyre rotation NZ",
    canonicalPath: "/",
    noindex: true,
  },
};

/**
 * Per-page SEO tag emitter (Feb 2026).
 *
 * Emits, for the current URL:
 *  - <title>
 *  - <meta name="description">
 *  - <meta name="keywords">
 *  - <meta name="robots"> (index,follow OR noindex,nofollow)
 *  - <link rel="canonical" href="{absolute self URL}">
 *  - <meta property="og:*"> and Twitter cards (URL-specific)
 *
 * Callers can override title/description/canonical for dynamic pages.
 */
export default function SEO({
  page = "home",
  customTitle,
  customDescription,
  customCanonicalPath, // e.g. "/tyre-quotes-auckland" — overrides config.canonicalPath
  noindex, // optional override
}) {
  const config = SEO_CONFIG[page] || SEO_CONFIG.home;
  const title = customTitle || config.title;
  const description = customDescription || config.description;
  const keywords = config.keywords;
  const path = customCanonicalPath || config.canonicalPath || "/";
  const canonicalUrl = `${CANONICAL_ORIGIN}${path === "/" ? "/" : path}`;
  const isNoindex = typeof noindex === "boolean" ? noindex : Boolean(config.noindex);

  return (
    <Helmet>
      {/* Primary */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={isNoindex ? "noindex, nofollow" : "index, follow"} />

      {/* Canonical — SELF, absolute */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph — URL-specific */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={LOGO_URL} />
      <meta property="og:site_name" content="TyreMatch" />
      <meta property="og:locale" content="en_NZ" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={LOGO_URL} />
    </Helmet>
  );
}

export { SEO_CONFIG, CANONICAL_ORIGIN };
